import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { ConvexAuthProvider } from "@convex-dev/auth/react";
import { Authenticated, AuthLoading, Unauthenticated } from "convex/react";
import { CircleNotch } from "@phosphor-icons/react";
import { Toaster } from "~/components/ui/sonner";
import { SignInScreen } from "~/components/auth/SignInScreen";
import { Wordmark } from "~/components/Wordmark";

import { routeTree } from "./routeTree.gen";
import { getConvexClient } from "./lib/convex";
import "./styles.css";

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("app");
if (!rootEl) {
  throw new Error("#app mount point missing");
}

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <ConvexAuthProvider client={getConvexClient()}>
      {/* Auth gate: the whole app sits behind GitHub OAuth + hyrdrocks org
          membership. Convex's <Authenticated> / <Unauthenticated> / <AuthLoading>
          are the React primitives that mirror the auth state from Convex. */}
      <AuthLoading>
        <div className="grid min-h-dvh place-items-center bg-background">
          <div className="flex flex-col items-center gap-3">
            <Wordmark className="text-2xl" />
            <CircleNotch
              size={18}
              weight="bold"
              className="animate-spin text-muted-foreground"
              aria-hidden
            />
            <p className="text-xs text-muted-foreground" role="status">
              Loading…
            </p>
          </div>
        </div>
      </AuthLoading>
      <Unauthenticated>
        <SignInScreen />
      </Unauthenticated>
      <Authenticated>
        <RouterProvider router={router} />
      </Authenticated>
      <Toaster richColors position="bottom-right" />
    </ConvexAuthProvider>
  </React.StrictMode>,
);
